import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import { brand, neutral, statusTone } from '../../theme';
import { formatBytes, formatDate } from '../../utils/format';
import StatusChip from '../ui/StatusChip';
import MulticamReviewPlayer from '../Multicam/MulticamReviewPlayer';
import { recordingAngle } from '../Multicam/separateAudio';
import type { RevisionLike, RevisionVersion } from '../EditorWorkspace/workflow';
import type { MulticamSnapshot, ReviewAnnotation } from './types';
import type { RevisionFiles } from './useRevisionFiles';
import ReviewStage from './ReviewStage';
import { ORIGINAL_KEY } from './reviewModel';

interface ReviewPlayerProps {
  /** Newest first (v3, v2, v1). */
  versions: RevisionVersion<RevisionLike>[];
  /** A revision id, ORIGINAL_KEY, or null when there is nothing to show. */
  selectedKey: string | null;
  onSelect: (key: string) => void;
  files: RevisionFiles;
  /** Change `nonce` to load and play `key` (e.g. a Play button elsewhere on the page). */
  playRequest?: { key: string; nonce: number } | null;
  /** Change `nonce` to show a moment of the original footage. */
  seekRequest?: { time: number; nonce: number } | null;
  original: { videoUrl?: string | null; videoName?: string | null; multicam?: MulticamSnapshot | null };
  /** Review notes, drawn on the original footage (they are timed against it). */
  notes: ReviewAnnotation[];
  fps: number;
  onDownload: (revision: RevisionLike, version: number) => void;
  downloadingId?: string | null;
}

const NO_NOTES: ReviewAnnotation[] = [];

const onStage = {
  title: { color: 'common.white', fontWeight: 600, fontSize: '0.9375rem' },
  body: { color: 'rgba(255,255,255,0.72)', fontSize: '0.8125rem', maxWidth: 420 },
} as const;

/** Centred content on the black stage (no video yet, loading, errors). */
const StageMessage: React.FC<{ icon?: React.ReactNode; title: string; body?: React.ReactNode; action?: React.ReactNode }> = ({
  icon,
  title,
  body,
  action,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1, px: 3 }}>
    {icon && <Box sx={{ color: 'rgba(255,255,255,0.8)', '& svg': { fontSize: 28 } }}>{icon}</Box>}
    <Typography sx={onStage.title}>{title}</Typography>
    {body && <Typography sx={onStage.body}>{body}</Typography>}
    {action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
  </Box>
);

const onStageFocus = { outline: '2px solid', outlineColor: 'common.white', outlineOffset: 2 } as const;

const stageButtonSx = {
  color: 'common.white',
  borderColor: 'rgba(255,255,255,0.5)',
  '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.08)' },
  '&:focus-visible': onStageFocus,
} as const;

const unplayableCopy = (status: string | null | undefined, version: number) => {
  switch (String(status || '').toUpperCase()) {
    case 'UPLOADING':
      return { title: `v${version} is still uploading`, body: 'You can watch it once your editor’s upload finishes.' };
    case 'PROCESSING':
      return { title: `v${version} is processing`, body: 'Check back in a few minutes.' };
    case 'FAILED':
      return { title: `v${version} didn’t upload`, body: 'Your editor can upload it again.' };
    default:
      return { title: `v${version} has no file to play`, body: undefined };
  }
};

/**
 * The review player: the latest cut first, a switcher for every version and
 * the original footage, and the studio's transport.
 *
 * A cut is fetched with the viewer's token and played from memory, because
 * the revision endpoint can't be streamed (see revisionMedia.ts). It only
 * starts loading when someone asks to play it, since cuts can be gigabytes.
 * The original footage streams from its signed link.
 */
const ReviewPlayer: React.FC<ReviewPlayerProps> = ({
  versions,
  selectedKey,
  onSelect,
  files,
  playRequest = null,
  seekRequest = null,
  original,
  notes,
  fps,
  onDownload,
  downloadingId = null,
}) => {
  const { states, load, cancel } = files;
  const [pendingPlay, setPendingPlay] = useState<string | null>(null);

  const versionById = useMemo(() => new Map(versions.map((entry) => [entry.revision.id, entry])), [versions]);
  const latestId = versions[0]?.revision.id;
  const multicam = original.multicam && (original.multicam.angles?.length ?? 0) > 0 ? original.multicam : null;
  // The set's separate recording, heard instead of an angle.
  const multicamRecording = useMemo(() => recordingAngle(multicam?.audioSource), [multicam?.audioSource]);
  const hasOriginal = Boolean(original.videoUrl || multicam);
  const isOriginal = selectedKey === ORIGINAL_KEY && hasOriginal;
  const selected = selectedKey && !isOriginal ? versionById.get(selectedKey) : undefined;
  const fileState = selected ? states[selected.revision.id] : undefined;
  const currentUrl = isOriginal
    ? (multicam ? null : original.videoUrl || null)
    : fileState?.status === 'ready'
      ? fileState.url || null
      : null;

  const startPlayback = useCallback(
    (key: string) => {
      // Playing makes the choice explicit, so a page refresh (a new message,
      // a new cut) never switches the player away from what's being watched.
      onSelect(key);
      setPendingPlay(key);
      if (key === ORIGINAL_KEY) return;
      const entry = versionById.get(key);
      if (!entry?.revision.previewUrl) {
        setPendingPlay(null);
        return;
      }
      // Failures are kept in the file state and shown on the stage.
      load(entry.revision).catch(() => setPendingPlay((current) => (current === key ? null : current)));
    },
    [versionById, load, onSelect]
  );

  // Play buttons elsewhere on the page.
  const handledPlayNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!playRequest || playRequest.nonce === handledPlayNonce.current) return;
    handledPlayNonce.current = playRequest.nonce;
    startPlayback(playRequest.key);
  }, [playRequest, startPlayback]);

  // A play that was asked for a version the viewer has since left is dropped.
  useEffect(() => {
    setPendingPlay((current) => (current && current !== selectedKey ? null : current));
  }, [selectedKey]);

  const clearPendingPlay = useCallback(() => setPendingPlay(null), []);

  // A seek is handed to the stage once; coming back to the footage later
  // must not jump to an old note again.
  const handledSeekNonce = useRef<number | null>(null);
  const markSeekHandled = useCallback((nonce: number) => {
    handledSeekNonce.current = nonce;
  }, []);
  const stageSeek =
    isOriginal && !multicam && seekRequest && seekRequest.nonce !== handledSeekNonce.current ? seekRequest : null;

  const placeholder = (() => {
    if (!selectedKey || (!selected && !isOriginal)) {
      return (
        <StageMessage
          icon={<MovieOutlinedIcon />}
          title="Nothing to watch yet"
          body="Cuts from your editor appear here as soon as they’re uploaded."
        />
      );
    }
    if (!selected) return null;
    const { revision, version } = selected;
    if (!revision.previewUrl) {
      const copy = unplayableCopy(revision.status, version);
      return <StageMessage icon={<HourglassEmptyOutlinedIcon />} title={copy.title} body={copy.body} />;
    }
    if (fileState?.status === 'loading') {
      const percent = fileState.progress === null ? null : Math.round(fileState.progress * 100);
      return (
        <StageMessage
          icon={
            <CircularProgress
              size={36}
              thickness={4}
              variant={percent === null ? 'indeterminate' : 'determinate'}
              value={percent ?? undefined}
              sx={{ color: 'common.white' }}
              aria-label={`Loading v${version}`}
            />
          }
          title={percent === null ? `Loading v${version}…` : `Loading v${version}… ${percent}%`}
          body={revision.sizeBytes ? `${formatBytes(revision.sizeBytes)} · the whole file loads before it plays` : undefined}
          action={
            <Button size="small" variant="outlined" sx={stageButtonSx} onClick={() => cancel(revision.id)}>
              Cancel
            </Button>
          }
        />
      );
    }
    if (fileState?.status === 'error') {
      return (
        <StageMessage
          icon={<ErrorOutlineOutlinedIcon />}
          title={`Couldn’t load v${version}`}
          body={fileState.error}
          action={
            <Button size="small" variant="outlined" sx={stageButtonSx} onClick={() => startPlayback(revision.id)}>
              Try again
            </Button>
          }
        />
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
        <IconButton
          aria-label={`Play v${version}`}
          onClick={() => startPlayback(revision.id)}
          sx={{
            width: 72,
            height: 72,
            bgcolor: 'common.white',
            color: brand.ink,
            '& svg': { fontSize: 38 },
            '&:hover': { bgcolor: neutral[100] },
            '&:focus-visible': { ...onStageFocus, outlineOffset: 3 },
          }}
        >
          <PlayArrowOutlinedIcon />
        </IconButton>
        <Typography sx={onStage.body}>
          {[`v${version}`, revision.sizeBytes ? formatBytes(revision.sizeBytes) : null].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
    );
  })();

  const handleSwitch = (_: React.MouseEvent<HTMLElement>, key: string | null) => {
    if (key) onSelect(key);
  };

  const revision = selected?.revision;
  const decisionNotes = selected?.decisions ?? [];

  return (
    <Card data-testid="review-player" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: 2,
          rowGap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${brand.border}`,
        }}
      >
        {(versions.length > 0 || hasOriginal) && (
          <Box sx={{ maxWidth: '100%', minWidth: 0, overflowX: 'auto', py: 0.25 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selectedKey}
              onChange={handleSwitch}
              aria-label="Version"
              sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, whiteSpace: 'nowrap', fontWeight: 600, color: 'text.secondary' } }}
            >
              {versions.map(({ revision: entry, version }) => (
                <ToggleButton key={entry.id} value={entry.id}>
                  {entry.id === latestId ? `v${version} · Latest` : `v${version}`}
                </ToggleButton>
              ))}
              {hasOriginal && <ToggleButton value={ORIGINAL_KEY}>Original footage</ToggleButton>}
            </ToggleButtonGroup>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.25, rowGap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
          {revision && selected ? (
            <>
              <StatusChip kind="revision" status={revision.status} />
              <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {[revision.createdAt ? formatDate(revision.createdAt) : null, revision.sizeBytes ? formatBytes(revision.sizeBytes) : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {revision.previewUrl && (
                <Button
                  size="small"
                  startIcon={downloadingId === revision.id ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
                  onClick={() => onDownload(revision, selected.version)}
                  disabled={Boolean(downloadingId)}
                >
                  Download
                </Button>
              )}
            </>
          ) : isOriginal ? (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: { xs: '100%', sm: 420 } }}>
              {multicam
                ? `${multicam.name || 'Multicam set'} · ${multicam.angles?.length ?? 0} angles`
                : original.videoName || 'Original footage'}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {isOriginal && multicam ? (
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <MulticamReviewPlayer
            angles={(multicam.angles || []).map((angle) => ({ ...angle, videoUrl: angle.videoUrl || '' }))}
            audioAngleKey={multicam.audioAngleKey || 'A'}
            audioSource={multicamRecording}
            timelineDurationMs={multicam.timelineDurationMs}
            annotations={[]}
            seekRequest={seekRequest ? { timeMs: Math.round(seekRequest.time * 1000), nonce: seekRequest.nonce } : null}
            fps={fps}
            // Already inside this player's card: no second frame.
            variant="plain"
          />
        </Box>
      ) : (
        <ReviewStage
          // A fresh stage per source (see ReviewStage).
          key={`${selectedKey ?? 'none'}:${currentUrl ?? 'empty'}`}
          videoUrl={currentUrl}
          fps={fps}
          notes={isOriginal ? notes : NO_NOTES}
          placeholder={placeholder}
          autoPlay={Boolean(currentUrl) && pendingPlay !== null && pendingPlay === selectedKey}
          onAutoPlayed={clearPendingPlay}
          seekRequest={stageSeek}
          onSeekHandled={markSeekHandled}
        />
      )}

      {revision && (revision.notes || decisionNotes.length > 0) && (
        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${brand.border}`, display: 'grid', gap: 1.25 }}>
          {revision.notes && (
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Editor’s note:{' '}
              </Box>
              {revision.notes}
            </Typography>
          )}
          {decisionNotes.map((note) => {
            const changes = String(note.type || '').toUpperCase() === 'REVISION_REQUEST';
            const tone = changes ? statusTone.attention : statusTone.done;
            return (
              <Box key={note.id} sx={{ pl: 1.5, borderLeft: `3px solid ${tone.main}` }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: tone.fg }}>
                  {changes ? 'Changes requested' : 'Approved'}
                  {note.authorName ? ` by ${note.authorName}` : ''}
                  {note.timestamp ? ` · ${formatDate(note.timestamp)}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {note.message}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Card>
  );
};

export default ReviewPlayer;
